// NOMBASE — Contact modal.
//
// The form itself, with `open` controlled by whoever renders it. Extracted
// from ContactFAB when "Contact us" moved into the profile menu: two entry
// points now open the same modal, and duplicating this form so they could
// would have meant two copies of the submit path drifting apart.
//
// Entry points:
//   • the profile dropdown (AppShell) — for signed-in users
//   • ContactFAB — the floating button, which now shows only when signed OUT,
//     so prospects on the auth/landing screens keep their route to support
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  { value: 'bug',         label: 'Bug report' },
  { value: 'feature',     label: 'Feature request' },
  { value: 'billing',     label: 'Billing or subscription' },
  { value: 'account',     label: 'Account or sign-in help' },
  { value: 'sales',       label: 'Sales or demo request' },
  { value: 'partnership', label: 'Partnership inquiry' },
  { value: 'general',     label: 'General inquiry' },
  { value: 'other',       label: 'Something else' },
];

// Read any cached identity so signed-in users get pre-filled name/email.
const cachedIdentity = () => {
  try {
    const u = JSON.parse(localStorage.getItem('nombase_user') || 'null');
    return { name: u?.name || '', email: u?.email || '' };
  } catch { return { name: '', email: '' }; }
};

export default function ContactModal({ open, onClose }) {
  const [busy, setBusy]   = useState(false);
  const [done, setDone]   = useState(false);
  const [err,  setErr]    = useState(null);
  const [form, setForm]   = useState(() => {
    const c = cachedIdentity();
    return { name: c.name, email: c.email, company: '', reason: 'general', message: '' };
  });

  useEffect(() => {
    if (!open) return;
    // Re-prime fields when the modal is opened (post-sign-in cases).
    if (!form.name || !form.email) {
      const c = cachedIdentity();
      setForm((f) => ({ ...f, name: f.name || c.name, email: f.email || c.email }));
    }
    setDone(false);
    setErr(null);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const headers = { 'Content-Type': 'application/json' };
      const res = await fetch(`${API}/api/contact`, { credentials: 'include',
        method: 'POST',
        headers,
        body: JSON.stringify({
          name:    form.name.trim(),
          email:   form.email.trim(),
          company: form.company.trim() || undefined,
          reason:  form.reason,
          message: form.message.trim(),
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${res.status}`);
      }
      setDone(true);
      setTimeout(() => onClose(), 1400);
    } catch (e2) {
      setErr(e2.message || 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <>
      {/* ── Modal ────────────────────────────────────────────────── */}
      {open && createPortal(
        <div
          className="fixed inset-0 z-50 bg-slate-900/45 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
          onClick={() => !busy && onClose()}
          data-testid="contact-modal"
        >
          <div
            className="bg-card w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl animate-in slide-in-from-bottom-4 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Contact</div>
                <h2 className="text-[16px] font-semibold text-slate-900 mt-0.5">How can we help?</h2>
              </div>
              <button
                onClick={() => !busy && onClose()}
                className="text-slate-400 hover:text-slate-700"
                data-testid="contact-close"
              >
                <X className="w-5 h-5" />
              </button>
            </header>

            {done ? (
              <div className="px-6 py-10 text-center" data-testid="contact-success">
                <div className="w-12 h-12 rounded-full bg-emerald-50 dark:bg-success/15 ring-1 ring-emerald-100 dark:ring-success/30 mx-auto flex items-center justify-center">
                  <Check className="w-5 h-5 text-emerald-600" strokeWidth={2.5} />
                </div>
                <p className="mt-4 text-[15px] font-semibold text-slate-900">Thanks — we got it.</p>
                <p className="mt-1 text-[13px] text-slate-500">We'll reply within one business day.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Name</label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={update('name')}
                      className="mt-1 w-full h-10 border-b border-slate-200 focus:border-slate-900 outline-none text-[14px] text-slate-900 bg-transparent transition-colors"
                      data-testid="contact-name"
                    />
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Email</label>
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={update('email')}
                      className="mt-1 w-full h-10 border-b border-slate-200 focus:border-slate-900 outline-none text-[14px] text-slate-900 bg-transparent transition-colors"
                      data-testid="contact-email"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Company <span className="text-slate-300 font-normal normal-case tracking-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={update('company')}
                    className="mt-1 w-full h-10 border-b border-slate-200 focus:border-slate-900 outline-none text-[14px] text-slate-900 bg-transparent transition-colors"
                    data-testid="contact-company"
                  />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Reason</label>
                  <select
                    value={form.reason}
                    onChange={update('reason')}
                    className="mt-1 w-full h-10 border-b border-slate-200 focus:border-slate-900 outline-none text-[14px] text-slate-900 bg-transparent appearance-none transition-colors cursor-pointer"
                    data-testid="contact-reason"
                  >
                    {REASONS.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-500">Message</label>
                  <textarea
                    required
                    rows={4}
                    maxLength={4000}
                    value={form.message}
                    onChange={update('message')}
                    placeholder="Tell us a bit about what's going on…"
                    className="mt-1 w-full p-3 rounded-lg ring-1 ring-slate-200 focus:ring-slate-900 outline-none text-[14px] text-slate-900 bg-card placeholder:text-slate-400 resize-none transition-colors"
                    data-testid="contact-message"
                  />
                </div>
                {err && (
                  <div className="text-[12px] text-rose-700 bg-rose-50 ring-1 ring-rose-100 rounded-md px-3 py-2" data-testid="contact-error">
                    {err}
                  </div>
                )}
                <div className="pt-1 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => !busy && onClose()}
                    className="px-4 py-2 text-[13px] font-medium text-slate-600 hover:text-slate-900"
                    disabled={busy}
                    data-testid="contact-cancel"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={busy}
                    className="bg-primary text-primary-foreground px-5 py-2 rounded-sm text-[13px] font-semibold hover:bg-primary/90 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                    data-testid="contact-submit"
                  >
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    {busy ? 'Sending…' : 'Send message'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
